/**
 * ⟦A-16⟧ **Le récapitulatif des jours écoulés** — « montrer le calcul, jamais le solde ».
 *
 * *« Il ne faut pas que le user se dise : je suis censé perdre 10 points mais en fait j'en perds
 * que 7, parce qu'il ne voit pas qu'il en perd 10 et qu'il en regagne 3 grâce aux nuits imposées.
 * Il faut bien décrire tout le calcul. »* (Mathieu.)
 *
 * Depuis ⟦A-14⟧ une longue coûte 48 h et un evergreen 64 h : le joueur clique **une fois** et le
 * monde avance de trois ou quatre jours. Ce panneau est **le seul endroit où il apprend que le
 * temps coûte** — les pressions d'⟦A-15⟧ (idées qui se périment, fenêtres qui se referment,
 * tendances qui tournent, rival qui monte) se produisent précisément pendant ces productions
 * longues : sans lui, elles sont invisibles, donc elles n'existent pas.
 *
 * ## Trois choses que ce composant ne fait pas
 *
 * 1. **Il ne calcule rien.** Chaque delta vient de `RecapLigne.delta`, mesuré par le moteur sur
 *    l'état lui-même. Le solde d'en-tête vient de `soldeParJauge` (moteur), et il n'est là que
 *    **au-dessus** du détail qui le décompose — A-16 interdit de montrer le solde seul.
 * 2. **Il n'agrège pas.** Le journal fond les versements en une ligne, « sinon dormir trois jours
 *    noie le journal » ; ici c'est l'inverse, **le détail EST le sujet**.
 * 3. **Il ne s'ouvre pas pour rien.** `jours_traverses === 0` ⇒ aucun panneau. Dormir ne fait pas
 *    avancer l'horloge (§2.1) et deux shorts tiennent dans une journée : le panneau ne sort que
 *    quand une action a réellement fait basculer un jour.
 *
 * ## Sa place dans la file des moments
 *
 * Il passe **après** la leçon du mode découverte (⟦A-9⟧ explique *avant* que ça arrive) et
 * **avant** le palier, le déverrouillage et l'événement : on raconte d'abord ce qui vient de se
 * passer, on fête ensuite. Il attend aussi `momentsSuspendus`, donc il ne s'ouvre jamais
 * par-dessus l'Atelier ni l'écran de publication ⟦A-8⟧ — il accueille le joueur au retour dans
 * sa chambre, ce qui est exactement le moment où il se demande « pourquoi j'ai perdu ça ? ».
 *
 * ## ⟦C57 · lot 3 · A-174⟧ Le dialogue le plus répété de la partie, ramené à l'essentiel
 *
 * 46 passages en 242 actions, 129 mots visibles : c'est LUI qui fait « on ne lit plus rien à la fin ».
 * À l'écran il ne reste que : le titre (« 3 jours ont passé »), UNE phrase (« J1 à J4 · 12 h de travail. »),
 * les soldes en nombres et en icônes, ce que le temps a pris (une idée périmée, une fenêtre refermée) et
 * « J'ai vu ». Le calcul COMPLET, ligne par ligne (ce que ça t'a coûté, le monde jour par jour, la mise en
 * ligne), s'ouvre par « Les dessous » et rejoint le Journal (`game/journal.ts`, `entreesDeResolution`) : il ne
 * disparaît pas, il est à un clic, puis relisible plus tard. A-16 tient : le solde n'est jamais SEUL, le
 * détail qui le décompose est derrière le bouton collé juste dessous. Tout vient de `game/recapDessous.ts`
 * (pur, testé) : ce composant ne compose plus de lignes.
 */

import { useEffect, useRef } from "react";

import { JAUGE_RECAP } from "../game/recap";
import { blocsDuRecap, chapoDuRecap, pertesDuTemps, puceDeSolde, soldesVisibles, titreDuRecap } from "../game/recapDessous";
import { detailJournal } from "../game/journal";
import { useDecouverteStore } from "../stores/decouverteStore";
import { useRunStore, type RecapEnAttente } from "../stores/runStore";
import { useUIStore } from "../stores/uiStore";
import { DessousDuJournal } from "./DessousDuJournal";
import { Fenetre } from "./Fenetre";
import { useSfx } from "./SoundProvider";

export function RecapJours() {
  const enAttente = useRunStore((s) => s.recapEnAttente);
  const consommer = useRunStore((s) => s.consommerRecap);
  const chute = useRunStore((s) => s.chute);
  // L'Atelier et l'écran de publication tiennent l'écran : on ne s'invite pas par-dessus.
  const momentsSuspendus = useUIStore((s) => s.momentsSuspendus);
  // ⟦A-9⟧ La leçon explique AVANT. Tant qu'elle est là, le récapitulatif attend son tour.
  const lecon = useDecouverteStore((s) => s.courante) !== null;
  const bouton = useRef<HTMLButtonElement>(null);
  const sfx = useSfx();

  const visible = enAttente !== null && !momentsSuspendus && !lecon && chute === null;

  useEffect(() => {
    if (!visible) return;
    sfx("dialog_open");
    bouton.current?.focus();
  }, [visible, sfx]);

  useEffect(() => {
    if (!visible) return;
    const auClavier = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        consommer();
      }
    };
    window.addEventListener("keydown", auClavier);
    return () => window.removeEventListener("keydown", auClavier);
  }, [visible, consommer]);

  if (!visible || !enAttente) return null;
  return <Panneau enAttente={enAttente} onFermer={consommer} />;
}

function Panneau({
  enAttente,
  onFermer,
}: {
  enAttente: RecapEnAttente;
  onFermer(): void;
}) {
  const { recap, action } = enAttente;
  const soldes = soldesVisibles(recap);
  const pertes = pertesDuTemps(recap);
  // Le calcul complet : la MÊME donnée que l'entrée du Journal (`entreesDeResolution`), mise en page par le même composant.
  const detail = detailJournal("Les jours écoulés", blocsDuRecap(recap, action));

  // ⟦C48 · A-168 §3⟧ Le récapitulatif est une `Fenetre` (titre dans le bandeau, bouton « J'ai vu » dans
  // le pied fixe).
  return (
    <div className="overlay" role="presentation">
      <div className="dialogue-fenetre dialogue-fenetre-large">
        <Fenetre
          titre={titreDuRecap(recap.jours_traverses)}
          icone="🕰"
          role="dialog"
          ariaModal
          className="fenetre-dialogue recap-jours"
          pied={
            <button type="button" className="bouton primaire" onClick={onFermer}>
              J'ai vu
            </button>
          }
        >
          <p className="recap-chapeau">{chapoDuRecap(recap)}</p>

          {/* Le solde — et **uniquement** collé au détail qui le décompose (A-16) : le bouton est juste dessous. */}
          {soldes.length > 0 || pertes.length > 0 ? (
            <div className="recap-soldes">
              <ul className="recap-puces">
                {soldes.map(([jauge, valeur]) => (
                  <li key={jauge} className={valeur >= 0 ? "bien" : "mal"}>
                    <span>{puceDeSolde(jauge, valeur)}</span>
                    <span className="recap-puce-nom">{JAUGE_RECAP[jauge].nom}</span>
                  </li>
                ))}
                {pertes.map((p) => (
                  <li key={p.texte} className="mal recap-perte">
                    <span aria-hidden="true">{p.icone}</span>
                    <span className="recap-puce-nom">{p.texte}</span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {detail ? <DessousDuJournal detail={detail} variante="texte" className="recap-dessous" /> : null}
        </Fenetre>
      </div>
    </div>
  );
}
